import { rem } from 'polished'
import { EmptyArray } from 'lib/array/arrayUtils'
import { type ChangeEvent, type ReactNode, useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import AccommodationIndividualRoomTile from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTile'
import AccommodationIndividualRoomTileDetails from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileDetails'
import AccommodationIndividualRoomTileHeader from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileHeader'
import AccommodationIndividualRoomTileImage from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileImage'
import AccommodationIndividualRoomTilePricing from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTilePricing'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NestableClickable from 'components/Common/NestableClickable'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import Group from 'components/utils/Group'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalContext from 'contexts/ModalContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { occupantsFitAnyRoomCapacityByThresholds } from 'lib/offer/occupancyUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import {
  arePropertyRatesLoading,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { getActivePropertyRatesByRoom } from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyIndividualRoomTypeSelectionModal from './PropertyIndividualRoomTypeSelectionModal'
import PropertyOptionalBundleOption from './PropertyOptionalBundleOption'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import PropertyRatePrice from './PropertyRatePrice'
import { getSelectedOptionalBundlesPrice, hasSelectedPropertyTravelProducts } from './propertyMultiRoomOptionalBundles'
import { getPropertyRatePassportOffer } from './singlePropertyUtils'

interface Props {
  occupancyRoom: OfferPageRoom
  /** Per-room options from {@link getPropertyIndividualRowsByRoomId}. */
  roomOptions: Array<RoomRateViews>
  packageNumber: number
  activeRatesByRoom: ReturnType<typeof getActivePropertyRatesByRoom>
  rooms: Array<OfferPageRoom>
  property: App.Property.Property | undefined
  checkIn?: string
  availableForPurchase: boolean
  areAnytimeRates: boolean
  isLoading: boolean
  open?: boolean
}

type MappedProps = Pick<Props,
  | 'activeRatesByRoom'
  | 'rooms'
  | 'property'
  | 'checkIn'
  | 'availableForPurchase'
  | 'areAnytimeRates'
  | 'isLoading'
  | 'open'
>

function PropertyIndividualRoomRoom(props: Props) {
  const {
    occupancyRoom,
    roomOptions,
    activeRatesByRoom,
    rooms,
    packageNumber,
    property,
    checkIn,
    availableForPurchase,
    areAnytimeRates,
    isLoading,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()

  const noRoomAvailableHeading = intl.formatMessage({
    id: 'BO8EbAIynm7k_a17y_Za',
    defaultMessage: 'No room available',
    description: 'Heading shown when no room is available in multi-room booking',
  })

  const noRoomDueToCapacityOnly = useMemo(
    () =>
      roomOptions.length > 0 &&
      roomOptions.every(
        roomOption => !occupantsFitAnyRoomCapacityByThresholds(
          occupancyRoom,
          roomOption.propertyRoom.capacities,
          roomOption.propertyRoom.ageCategoryThresholds,
        ),
      ),
    [roomOptions, occupancyRoom],
  )

  const openNoRoomCheckinFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const openNoRoomCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const selectedRoomOption = useMemo((): RoomRateViews | undefined => {
    const active = activeRatesByRoom[occupancyRoom.id]
    if (active) {
      return roomOptions.find(r => r.roomId === active.roomId)
    }
    // No active rate yet: fall back to the occupancy room's pre-selected room type.
    if (occupancyRoom.roomId) {
      const selectedOption = roomOptions.find(r => r.roomId === occupancyRoom.roomId)
      if (selectedOption) {
        return selectedOption
      }
    }
    // Keep a room visible even without an active pick so the shopper can always change room/rate,
    // matching legacy bedbank multi-room. Genuinely empty options fall through to the error tile.
    return roomOptions[0]
  }, [activeRatesByRoom, roomOptions, occupancyRoom.id, occupancyRoom.roomId])

  const activeRate = activeRatesByRoom[occupancyRoom.id]

  const onToggleOptionalBundle = useCallback((bundleId: string, event: ChangeEvent<HTMLInputElement>) => {
    const occupancyRoomIndex = rooms.findIndex(({ id }) => id === occupancyRoom.id)
    if (occupancyRoomIndex === -1) return
    const previousIds = occupancyRoom.optionalBundleIds ?? []
    const nextIds = event.currentTarget.checked
      ? (previousIds.includes(bundleId) ? previousIds : [...previousIds, bundleId])
      : previousIds.filter(id => id !== bundleId)
    const newOccupancyRoom = {
      ...occupancyRoom,
      optionalBundleIds: nextIds.length ? nextIds : undefined,
    }
    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: [
        ...rooms.slice(0, occupancyRoomIndex),
        newOccupancyRoom,
        ...rooms.slice(occupancyRoomIndex + 1),
      ],
    })
  }, [rooms, occupancyRoom, offerPageDispatch])
  const selectedOptionalBundlesPrice = getSelectedOptionalBundlesPrice(activeRate, occupancyRoom.optionalBundleIds)

  const selectableOptionCount = useMemo(() => {
    if (!checkIn) {
      return roomOptions.length
    }
    // Count every selectable rate (not just room types) so one room type with multiple rates still
    // lets the shopper change rate, matching legacy bedbank multi-room's isOnlyOption behaviour.
    return roomOptions.reduce((total, r) => total + r.rates.length, 0)
  }, [checkIn, roomOptions])

  const canChangeRoom = selectableOptionCount > 1

  const roomOptionsAvailableText = canChangeRoom
    ? intl.formatMessage(
        {
          id: 'V8tTGHVdJnsKIhT-nQTN',
          defaultMessage: '{count, plural, one {# room option} other {# room options}} available',
          description: 'Text showing number of available room options in multi-room booking',
        },
        { count: selectableOptionCount },
      )
    : intl.formatMessage({
        id: 'oEViJkIj1a46YTYnM3WO',
        defaultMessage: 'No additional options',
        description: 'Text shown when no additional room options are available',
      })

  const onOpenChangeRoom = useCallback(() => {
    if (!selectedRoomOption) {
      return
    }
    showModal(<PropertyIndividualRoomTypeSelectionModal
      occupancyRoom={occupancyRoom}
      selectableRoomOptions={roomOptions}
      currentPropertyRoomTypeId={selectedRoomOption.propertyRoom.id}
    />)
  }, [roomOptions, occupancyRoom, selectedRoomOption, showModal])

  const checkAvailability = !checkIn || !hasSelectedRooms(rooms)

  const actionsState = useMemo((): AccommodationRoomTileActionsState | undefined => {
    if (!selectedRoomOption || isLoading) {
      return undefined
    }
    if (!availableForPurchase) {
      return 'sold-out'
    }

    // Dates checked before capacity: an over-capacity room still reports datesAvailable true.
    if (!selectedRoomOption.datesAvailable) {
      return 'sold-out-on-dates'
    }

    // Accommodation service owns the capacity verdict: it prices the rooms that hold the requested
    // occupancy and flags the rest `EXCEEDS_ROOM_CAPACITY`, which `getRatesByRoom` carries through as
    // `roomFitsCapacity`. Read that rather than the room's configured capacities, so a room the
    // service priced keeps its rates unwarned, matching the single-room path in `PropertyRoomOption`.
    if (!selectedRoomOption.roomFitsCapacity) {
      return 'exceeded-capacity'
    }

    if (!activeRate) {
      return undefined
    }
    const anytimeRatesActionState = areAnytimeRates ? 'available-on-dates-variant' : 'available-on-dates'
    if (checkAvailability) {
      return anytimeRatesActionState
    }
  }, [
    areAnytimeRates,
    availableForPurchase,
    checkAvailability,
    isLoading,
    activeRate,
    selectedRoomOption,
  ])

  const toggleFilterForSelectedRoom = useCallback((filter: OfferPageFilterType) => {
    if (!selectedRoomOption) {
      return
    }
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, selectedRoomOption])

  const handleTileChangeDatesClick = useCallback(() => {
    toggleFilterForSelectedRoom('checkin')
  }, [toggleFilterForSelectedRoom])

  const handleTileCheckAvailabilityClick = useCallback(() => {
    toggleFilterForSelectedRoom((areAnytimeRates || hasSelectedRooms(rooms)) ? 'checkin' : 'capacity')
  }, [areAnytimeRates, rooms, toggleFilterForSelectedRoom])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilterForSelectedRoom('capacity')
  }, [toggleFilterForSelectedRoom])

  const showRoomDetails = useCallback(() => {
    if (!selectedRoomOption) {
      return
    }
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={selectedRoomOption.propertyRoom} />)
  }, [selectedRoomOption, showModal])

  if (!property) {
    return null
  }

  const propertyTimezone = property.location.timezone
  const cancellationPolicy = activeRate && propertyTimezone ? (
    <PropertyCancellationPolicy
      rate={activeRate}
      propertyTimeZone={propertyTimezone}
      shouldClarifyRefundIsHotelOnly={hasSelectedPropertyTravelProducts(rooms, activeRatesByRoom)}
    />
  ) : undefined

  let rateDetails: ReactNode | undefined

  if (activeRate) {
    const passportOffer = getPropertyRatePassportOffer(activeRate, property)
    rateDetails = <>
      <PropertyRateInclusions
        property={property}
        inclusions={activeRate.inclusions}
        bonusInclusions={activeRate.bonusInclusions}
        luxPlusInclusions={activeRate.luxPlusInclusions}
        bundleInclusions={activeRate.bundleInclusions}
        passportOffer={passportOffer}
        maxHeight={208}
      />
      <PropertyOptionalBundleOption
        optionalBundles={activeRate.optionalBundles}
        selectedBundleIds={occupancyRoom.optionalBundleIds ?? EmptyArray}
        onToggleBundle={onToggleOptionalBundle}
      />
    </>
  }

  return (
    <>
      {!selectedRoomOption && (
        <AccommodationIndividualRoomTile>
          <AccommodationIndividualRoomTileHeader
            room={occupancyRoom}
            heading={noRoomAvailableHeading}
            packageNumber={packageNumber}
            saleUnit="room"
          />
          <AccommodationIndividualRoomTileDetails>
            {noRoomDueToCapacityOnly && (
              <MessageBanner kind="warning" density="compact">
                <FormattedMessage
                  id="sJcHOXDrvDIuaWuk78Mh"
                  defaultMessage="No room available, please <link>revise number of guests</link>"
                  description="Warning when no room is available suggesting to revise number of guests"
                  values={{
                    link: chunks => (
                      <TextLink size="medium" weight="regular" onClick={openNoRoomCapacityFilter}>
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </MessageBanner>
            )}
            {!noRoomDueToCapacityOnly && (
              <MessageBanner kind="warning" density="compact">
                <FormattedMessage
                  id="IOCri3xMqkBvpX0FbsE7"
                  defaultMessage="No room available, please <datesLink>try different dates</datesLink> or <guestsLink>revise number of guests</guestsLink>"
                  description="Warning when no room is available suggesting to try different dates or revise number of guests"
                  values={{
                    datesLink: chunks => (
                      <TextLink size="medium" weight="regular" onClick={openNoRoomCheckinFilter}>
                        {chunks}
                      </TextLink>
                    ),
                    guestsLink: chunks => (
                      <TextLink size="medium" weight="regular" onClick={openNoRoomCapacityFilter}>
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </MessageBanner>
            )}
          </AccommodationIndividualRoomTileDetails>
        </AccommodationIndividualRoomTile>
      )}
      {selectedRoomOption && (
        <Pane data-testid="package-option">
          <ScrollAnchor id={selectedRoomOption.propertyRoom.id} vOffset={rem(80)} />

          <AccommodationIndividualRoomTile roomPackage={selectedRoomOption.propertyRoom}>
            <AccommodationIndividualRoomTileHeader
              saleUnit="room"
              room={occupancyRoom}
              heading={selectedRoomOption.propertyRoom.name}
              occupancyFormatOptions={selectedRoomOption.propertyRoom.ageCategoryThresholds}
              occupancyOnSaleUnitLine
              onTitleClick={showRoomDetails}
              packageNumber={packageNumber}
            />
            <AccommodationIndividualRoomTileImage
              roomPackage={selectedRoomOption.propertyRoom}
              showBookmark={false}
            />
            <AccommodationIndividualRoomTileDetails>
              {!!selectedRoomOption.propertyRoom.stats.booked && (
                <LabelGroup>
                  <BookingsCountLabel bookings={selectedRoomOption.propertyRoom.stats.booked} threshold="room" />
                </LabelGroup>
              )}
              <NestableClickable onClick={showRoomDetails}>
                <AccommodationRoomDetailsList roomPackage={selectedRoomOption.propertyRoom} />
              </NestableClickable>
              <AccommodationRoomTileActions
                roomPackage={selectedRoomOption.propertyRoom}
                state={actionsState}
                isWalled={false}
                shouldShowAmenitiesAndPoliciesCta={false}
                onChangeOccupancyClick={onChangeOccupancyClick}
                onChangeDatesClick={handleTileChangeDatesClick}
                onCheckAvailabilityClick={handleTileCheckAvailabilityClick}
              />
              {!!activeRate?.name && (
                <BodyText variant="large" weight="bold" colour="normal-contrast">
                  {activeRate.name}
                </BodyText>
              )}
              {!!activeRate?.promotion && (
                <LabelGroup>
                  <PropertyPromotionLabel promotion={activeRate.promotion} />
                </LabelGroup>
              )}
              {cancellationPolicy}
              {activeRate?.promotion && <PropertyPromotionDetails promotion={activeRate.promotion} />}
              {rateDetails}
            </AccommodationIndividualRoomTileDetails>
            <AccommodationIndividualRoomTilePricing>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && activeRate && <PropertyRatePrice
                rate={activeRate}
                occupancyRoom={occupancyRoom}
                optInBundlesPrice={selectedOptionalBundlesPrice}
                optInBundleIds={occupancyRoom.optionalBundleIds}
              />}
              <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
                <TextButton
                  fit="full-width"
                  variant="default"
                  kind="secondary"
                  size="medium"
                  startIcon={<LineEditAltIcon />}
                  onClick={onOpenChangeRoom}
                  disabled={!canChangeRoom}
                >
                  {intl.formatMessage({
                    id: 'z25w3zmoOUVx1WEQiRCQ',
                    defaultMessage: 'Change room',
                    description: 'Button text to change room selection in multi-room booking',
                  })}
                </TextButton>
                <Caption variant="medium" colour="neutral-three">
                  {roomOptionsAvailableText}
                </Caption>
              </Group>
            </AccommodationIndividualRoomTilePricing>
          </AccommodationIndividualRoomTile>
        </Pane>
      )}
    </>
  )
}

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  activeRatesByRoom: getActivePropertyRatesByRoom(state, appState),
  open: !!state.activeFilter,
}), PropertyIndividualRoomRoom, preventRenderWithOpenFilter)
